import { AppointmentStatusCodes, QueryFactory } from '@lmt/shared-types';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import React, { useCallback } from 'react';
import {
  getFormattedTimeRange,
  getLongDateWithoutYearRange,
} from '@lmt/unicorn-shared/app/components/patients/common';
import { VisitStatusOption } from '../../cases/components/case-tree/VisitStatusOption';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';

import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

import styles from './PatientVisitItem.module.scss';

import SaveIcon from '@lmt/unicorn-shared/app/icons/small-tick.svg?react';
import CancelIcon from '@lmt/unicorn-shared/app/icons/red-cross.svg?react';

export interface VisitInfo {
  id: number;
  type: string;
  statusId: AppointmentStatusCodes;
  caseTitle: string;
  startDate: Date;
  endDate: Date;
  dataBlocks: string[];
  caseId: number;
}

export interface Props {
  visit: VisitInfo;
  patientId: number;
  openCalendarTab: (date: Date) => Promise<void>;
}

export const PatientVisitItem: React.FC<Props> = (props) => {
  const { visit, openCalendarTab } = props;
  const forms = useGlobalForms();
  const dateNow = new Date();
  const i18n = useScopedTranslation('patients.overview.visits');
  const modals = useModal();

  const cancelVisit = useCallback(async () => {
    const isConfirmed = await modals.showConfirm({
      title: i18n.t('confirm_cancel_visit_title'),
      text: i18n.t('confirm_cancel_visit_text'),
      okButtonText: i18n.t('confirm_cancel_visit_button'),
    });
    if (isConfirmed) {
      try {
        await QueryFactory.AppointmentEventsClient.cancelVisit(visit.id);
        if (visit.startDate > new Date()) {
          await openCalendarTab(visit.startDate);
        }
      } catch (err) {
        await modals.showError({ text: convertToErrorString(err) });
      }
    }
  }, []);

  const completeVisit = useCallback(async () => {
    const isConfirmed = await modals.showConfirm({
      title: i18n.t('confirm_complete_visit_title'),
      text: i18n.t('confirm_complete_visit_text'),
      okButtonText: i18n.t('confirm_complete_visit_button'),
      okButtonColor: ButtonColor.Secondary,
    });
    if (isConfirmed) {
      try {
        await QueryFactory.AppointmentEventsClient.completeVisit(visit.id);
      } catch (err) {
        await modals.showError({ text: convertToErrorString(err) });
      }
    }
  }, []);

  const confirmVisit = useCallback(async () => {
    const isConfirmed = await modals.showConfirm({
      title: i18n.t('confirm_confirm_visit_title'),
      text: i18n.t('confirm_confirm_visit_text'),
      okButtonText: i18n.t('confirm_confirm_visit_button'),
      okButtonColor: ButtonColor.Secondary,
    });
    if (isConfirmed) {
      try {
        await QueryFactory.AppointmentEventsClient.confirmVisit(visit.id);
      } catch (err) {
        await modals.showError({ text: convertToErrorString(err) });
      }
    }
  }, []);

  const dateColumn = (
    <div className={styles.dateRangeContainer} data-test-id={'date'}>
      <div className={styles.timeRange}>
        {getFormattedTimeRange(visit.startDate, visit.endDate)}
      </div>
      <div className={styles.dateRange}>
        {getLongDateWithoutYearRange(visit.startDate, visit.endDate)}
      </div>
      <div className={styles.year}>{visit.startDate.getFullYear()}</div>
    </div>
  );

  const statusColumn = (
    <div className={styles.statusContainer} data-test-id="status-container">
      {dateNow > visit.endDate &&
      (visit.statusId === AppointmentStatusCodes.Planned ||
        visit.statusId === AppointmentStatusCodes.Confirmed) ? (
        <div className={styles.statusContainer}>
          <UniLink
            icon={<CancelIcon />}
            title={i18n.t('cancel_button')}
            color={ButtonColor.Danger}
            className={styles.link}
            onClick={(event) => {
              event.stopPropagation();
              cancelVisit().catch((e) => console.error('cancelVisit', e));
            }}
          />
          <UniLink
            icon={<SaveIcon />}
            title={i18n.t('complete_button')}
            color={ButtonColor.Secondary}
            className={styles.link}
            onClick={(event) => {
              event.stopPropagation();
              completeVisit().catch((e) => console.error('completeVisit', e));
            }}
          />
        </div>
      ) : (
        <div className={styles.statusContainer}>
          <VisitStatusOption status={visit.statusId} />

          {visit.statusId !== AppointmentStatusCodes.Canceled &&
            visit.statusId !== AppointmentStatusCodes.Completed && (
              <UniLink
                icon={<CancelIcon />}
                title={i18n.t('cancel_button')}
                color={ButtonColor.Danger}
                className={styles.link}
                onClick={(event) => {
                  event.stopPropagation();
                  cancelVisit().catch((e) =>
                    console.error('PatientVisitItem.statusColumn1', e),
                  );
                }}
              />
            )}
          {visit.statusId === AppointmentStatusCodes.Planned && (
            <UniLink
              icon={<SaveIcon />}
              title={i18n.t('confirm_button')}
              color={ButtonColor.Secondary}
              className={styles.link}
              onClick={(event) => {
                event.stopPropagation();
                confirmVisit().catch((e) =>
                  console.error('PatientVisitItem.statusColumn2', e),
                );
              }}
            />
          )}
        </div>
      )}
    </div>
  );

  const visitInfo = (
    <div className={styles.visitInfoContainer} data-test-id={'visit-info'}>
      <div className={styles.divider}>
        <div
          data-status={
            visit.statusId === AppointmentStatusCodes.Canceled
              ? 'canceled'
              : visit.statusId === AppointmentStatusCodes.Completed
                ? 'completed'
                : undefined
          }
          className={styles.ellipse}
        ></div>
      </div>
      <div className={styles.visitInfo}>
        <div className={styles.caseTitle} data-test-id={'case-title'}>
          {visit.caseTitle}
        </div>
        <div
          data-planned={
            dateNow < visit.endDate &&
            visit.statusId === AppointmentStatusCodes.Planned
          }
          className={styles.visitType}
          data-test-id={'visit-type'}
        >
          {visit.type}
        </div>
        <div className={styles.datablocks} data-test-id={'data-blocks'}>
          {visit.dataBlocks.length > 0
            ? visit.dataBlocks.map((dataBlock, index) => {
                return (
                  <div key={index} className={styles.datablock}>
                    {dataBlock}
                  </div>
                );
              })
            : '-'}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={styles.container}
      onClick={() => forms.openVisitEditing(visit.id)}
      data-test-id={'patient-visit-item'}
    >
      {dateColumn}
      <div className={styles.row}>
        {visitInfo}
        {statusColumn}
      </div>
    </div>
  );
};
